import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
  type ViewStyle,
} from 'react-native';
import { useTranslation } from 'react-i18next';

import type { StreamCamera } from '@/entities/stream/types';
import { AddCameraSheet } from '@/features/add-camera/ui/AddCameraSheet';
import { useBreakpoint } from '@/shared/lib/breakpoints';
import { IconButton } from '@/shared/ui/button';
import { EmptyState } from '@/shared/ui/empty-state';
import { Icon, type IconName } from '@/shared/ui/icons';
import {
  Menu,
  MenuLabel,
  MenuOption,
  MenuSeparator,
  useAnchor,
} from '@/shared/ui/menu';
import { SkeletonList } from '@/shared/ui/skeleton';
import { AppText } from '@/shared/ui/text';
import { radii, spacing, useTheme } from '@/shared/ui/theme';
import { camerasOfDepartment, useCameraCatalog } from '../lib/catalog';
import { CAN_FULLSCREEN, toggleFullscreen } from '../lib/fullscreen';
import {
  assign,
  clearSlot,
  emptySlots,
  fillPage,
  LAYOUT_IDS,
  LAYOUTS,
  layoutForCount,
  neighbour,
  nextWindow,
  pageCount,
  swap,
  windowCount,
  type LayoutId,
  type Slots,
} from '../lib/layouts';
import type { DragPayload } from '../lib/tile-dom';
import {
  CAROUSEL_STEPS,
  useLiveViewStore,
  type Preset,
} from '../lib/view-store';
import { canPtz, isKnownPtz } from '../lib/ptz';
import { usePtzChannelIds } from '../lib/ptz-channel';
import { CameraTree } from './CameraTree';
import { GRID_LINE, LiveTile, VIDEO_BG } from './LiveTile';
import { PtzPanel } from './PtzPanel';
import { ViewsMenu } from './ViewsMenu';

/**
 * Живой просмотр по образцу Hikvision (iVMS-4200 и веб-интерфейс регистратора):
 * дерево камер слева, раскладка окон справа, полоса управления под ней.
 *
 *   ┌───────────┬──────────────────────────────────────────┐
 *   │ поиск     │  ┌──────┬──────┐                         │
 *   │ ▾ NVR     │  │  1   │  2   │                         │
 *   │   канал 1 │  ├──────┼──────┤                         │
 *   │   канал 2 │  │  3   │  4   │                         │
 *   │ ▾ Камеры  │  └──────┴──────┘                         │
 *   │           │ ▤ [1][4][1+5][9][16] Виды▾ ‹1/2› ⟳ ⤢ ▶ ■ ▣ ⛶ ⋯ │
 *   └───────────┴──────────────────────────────────────────┘
 *
 * Привычки оттуда же, потому что их уже знают руки охранника:
 *
 *  - щелчок по окну выбирает его, щелчок по камере в дереве открывает её в выбранном
 *    окне, и выделение переходит к следующему пустому — камеры раскладываются подряд;
 *    на вебе камеру можно и перетащить в любое окно, а окно — на другое;
 *  - двойной щелчок по окну разворачивает его на всю раскладку, повторный — обратно;
 *  - ‹ › листают камеры страницами, ▶ открывает все, ■ закрывает;
 *  - «Виды» — сохранённые раскладки с камерами; карусель ⟳ листает виды (если их два
 *    и больше) или страницы — для поста с одним монитором;
 *  - ▣ — режим просмотра: раскладка во всё окно браузера без меню кабинета; ⛶ — во
 *    весь экран монитора;
 *  - клавиши: стрелки, Enter, Esc, Delete, F (см. `keys` ниже).
 *
 * Последний вид запоминается по компании (`view-store`). Первое открытие раскладывает
 * все камеры, которые в сети, — владелец просил, чтобы вкладка открывалась уже
 * играющей (2026-10-12). Каждое окно берёт ДОПОЛНИТЕЛЬНЫЙ поток: шестнадцать основных
 * положили бы канал объекта.
 *
 * Окно за развёрнутым соседом остаётся смонтированным и не рисуется — свернуть обратно
 * значит увидеть картинку сразу. С «экономией трафика» оно закрывается.
 *
 * На телефоне: раскладка во всю ширину, свайп листает страницы, ⛶ открывает её на
 * весь экран (и в альбомной ориентации); дерево под полосой. Раскладки «16» нет —
 * шестнадцать окон по 90pt не разглядеть.
 */
const fixedFill = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  borderRadius: 0,
  borderWidth: 0,
} as unknown as ViewStyle;

const styles = StyleSheet.create({
  desktop: {
    flex: 1,
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: radii.lg,
    overflow: 'hidden',
  },
  side: { width: 264, borderRightWidth: StyleSheet.hairlineWidth },
  main: { flex: 1, minWidth: 0 },
  stage: { flex: 1, minHeight: 0, backgroundColor: VIDEO_BG },
  box: { backgroundColor: GRID_LINE },
  cell: { position: 'absolute', padding: 1 },
  // Полоса управления — как статусная строка iVMS: 40pt, группы кнопок в тонких
  // рамках, между группами воздух, а не разделители через каждую кнопку.
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(2),
    minHeight: 40,
    paddingHorizontal: spacing(2),
    paddingVertical: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  barGroup: { flexDirection: 'row', alignItems: 'center', gap: 1 },
  segment: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.md,
    padding: 1,
    gap: 1,
  },
  spacer: { flex: 1 },
  hidden: { display: 'none' },
  layoutPick: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    height: 28,
    paddingHorizontal: spacing(2),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.md,
  },
  layoutBtn: {
    width: 30,
    height: 26,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  glyph: { width: 16, height: 12 },
  glyphCell: { position: 'absolute', borderWidth: 1.25, borderRadius: 1 },
  pager: { minWidth: 44, textAlign: 'center' },
  carousel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    height: 28,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
  },
  help: { padding: spacing(2), gap: spacing(1), maxWidth: 320 },
  phone: { gap: spacing(3), paddingTop: spacing(3) },
  phoneGrid: { width: '100%' },
  phoneStage: { backgroundColor: VIDEO_BG, overflow: 'hidden' },
  phonePanel: { borderWidth: 1, borderRadius: radii.lg, overflow: 'hidden' },
  full: { flex: 1, backgroundColor: VIDEO_BG },
  // Внизу справа: сверху у окон свои кнопки (SD/HD, ✕), и выход поверх них перекрывал
  // кнопки углового окна.
  fullBar: {
    position: 'absolute',
    bottom: spacing(3),
    right: spacing(3),
    flexDirection: 'row',
    gap: spacing(1),
  },
  fullBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  fullPager: {
    position: 'absolute',
    bottom: spacing(3),
    alignSelf: 'center',
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(1),
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
});

const PHONE_LAYOUTS: readonly LayoutId[] = ['1', '4', '6', '9'];
const SWIPE = 60;

/** Мини-рисунок раскладки на кнопке — как значки разделения экрана в iVMS. */
function LayoutGlyph({ id, color }: { id: LayoutId; color: string }) {
  const { cols, rows, cells } = LAYOUTS[id];
  const w = 16 / cols;
  const h = 12 / rows;
  return (
    <View style={styles.glyph}>
      {cells.map((c, i) => (
        <View
          key={i}
          style={[
            styles.glyphCell,
            {
              left: c.x * w,
              top: c.y * h,
              width: c.w * w,
              height: c.h * h,
              borderColor: color,
            },
          ]}
        />
      ))}
    </View>
  );
}

/** Значок «главная камера»: крупное окно сверху и три маленьких под ним. */
function FocusGlyph({ color }: { color: string }) {
  return (
    <View style={styles.glyph}>
      <View
        style={[
          styles.glyphCell,
          { left: 0, top: 0, width: 16, height: 8, borderColor: color },
        ]}
      />
      {[0, 1, 2].map(n => (
        <View
          key={n}
          style={[
            styles.glyphCell,
            {
              left: (n * 16) / 3,
              top: 8,
              width: 16 / 3,
              height: 4,
              borderColor: color,
            },
          ]}
        />
      ))}
    </View>
  );
}

function isHot(state: { pressed: boolean }): boolean {
  return state.pressed || (state as { hovered?: boolean }).hovered === true;
}

function sameView(a: Slots, b: Slots): boolean {
  return a.every((id, i) => id === b[i]);
}

export function LiveView({
  companyId,
  onOpenSettings,
  focusDepartment,
  onFocusHandled,
  onAdopt,
}: {
  companyId: string;
  /** Подключить устройство по QR — живёт в «+» списка камер. */
  onAdopt?: () => void;
  /** Настройки источника; `sourceId` — его `peer`. */
  onOpenSettings?: (sourceId: string, name: string) => void;
  /** Открыть камеры этого отдела (id проекции sv_control) — переход из карточки отдела. */
  focusDepartment?: string;
  onFocusHandled?: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const { isDesktop } = useBreakpoint();
  const win = useWindowDimensions();
  const [adding, setAdding] = useState(false);

  // ── Настройки смотрящего ──────────────────────────────────────────────────
  const hydrated = useLiveViewStore(s => s.hydrated);
  const hydrate = useLiveViewStore(s => s.hydrate);
  const saveView = useLiveViewStore(s => s.save);
  const prefs = useLiveViewStore(s => s.prefs);
  const setPrefs = useLiveViewStore(s => s.setPrefs);
  const allPresets = useLiveViewStore(s => s.presets);
  const savePreset = useLiveViewStore(s => s.savePreset);
  const deletePreset = useLiveViewStore(s => s.deletePreset);
  const presets = useMemo(
    () => allPresets[companyId] ?? [],
    [allPresets, companyId],
  );
  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // ── Камеры ────────────────────────────────────────────────────────────────
  const { catalog, isPending, isError, refetch } = useCameraCatalog(
    companyId,
    prefs.groupBy,
    {
      recorder: t('live.recorder.badge'),
      cameras: t('live.wall.cameras'),
      terminals: t('live.wall.terminals'),
      noChannels: t('live.recorder.noChannels'),
      noDepartment: t('live.view.noDepartment'),
    },
  );
  const { byId, groups, pending, order } = catalog;

  // ── Вид: раскладка, окна, выделение ──────────────────────────────────────
  const [layout, setLayout] = useState<LayoutId>('4');
  const [slots, setSlots] = useState<Slots>(emptySlots);
  const [selected, setSelected] = useState(0);
  const [maximized, setMaximized] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [ready, setReady] = useState(false);
  const [presetId, setPresetId] = useState<string | null>(null);
  const [carousel, setCarousel] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [phoneFull, setPhoneFull] = useState(false);

  const shownLayout: LayoutId = !isDesktop && layout === '16' ? '9' : layout;
  const count = windowCount(shownLayout);
  const pages = pageCount(order.length, count);

  // Один раз, когда известны и камеры, и сохранённое: либо прошлый вид, либо все
  // камеры в сети — в раскладку по их числу.
  useEffect(() => {
    if (ready || !hydrated || isPending) {
      return;
    }
    const saved = useLiveViewStore.getState().views[companyId];
    if (saved && saved.slots.some(id => id && byId.has(id))) {
      setLayout(saved.layout);
      setSlots(saved.slots);
    } else {
      const auto = layoutForCount(Math.min(order.length, 16));
      setLayout(auto);
      setSlots(fillPage(order, windowCount(auto), 0));
    }
    setReady(true);
  }, [ready, hydrated, isPending, companyId, byId, order]);

  useEffect(() => {
    if (ready) {
      saveView(companyId, { layout, slots });
    }
  }, [ready, companyId, layout, slots, saveView]);

  // Ручное действие останавливает карусель: человек взялся за раскладку сам.
  const manual = () => setCarousel(false);

  const openMany = useCallback(
    (ids: string[]) => {
      const fits = ids.length <= count;
      const target = fits
        ? shownLayout
        : layoutForCount(Math.min(ids.length, isDesktop ? 16 : 9));
      setLayout(target);
      setSlots(fillPage(ids, windowCount(target), 0));
      setSelected(0);
      setMaximized(null);
      setPage(0);
      setPresetId(null);
    },
    [count, shownLayout, isDesktop],
  );

  // Переход «камеры отдела» из карточки отдела.
  useEffect(() => {
    if (!ready || !focusDepartment) {
      return;
    }
    const ids = camerasOfDepartment(catalog, focusDepartment.split(',')).map(
      c => c.deviceId,
    );
    if (ids.length > 0) {
      setCarousel(false);
      openMany(ids);
    }
    onFocusHandled?.();
  }, [ready, focusDepartment, catalog, openMany, onFocusHandled]);

  const openIn = useMemo(() => {
    const m = new Map<string, number>();
    slots.slice(0, count).forEach((id, i) => {
      if (id) {
        m.set(id, i);
      }
    });
    return m;
  }, [slots, count]);

  const pick = (camera: StreamCamera) => {
    manual();
    const at = maximized ?? Math.min(selected, count - 1);
    const next = assign(slots, at, camera.deviceId);
    setSlots(next);
    if (maximized === null) {
      setSelected(nextWindow(next, count, at));
    }
  };

  /** Камера из дерева — в это окно; окно на окно — меняются местами. */
  const drop = (at: number, payload: DragPayload) => {
    manual();
    if (payload.kind === 'camera') {
      setSlots(cur => assign(cur, at, payload.id));
    } else if (payload.index !== at) {
      setSlots(cur => swap(cur, payload.index, at));
    }
    setSelected(at);
  };

  const changeLayout = (id: LayoutId) => {
    manual();
    if (!isDesktop) {
      setPrefs({ phoneFocus: false });
    }
    setLayout(id);
    setMaximized(null);
    setPage(0);
    setSelected(s => Math.min(s, windowCount(id) - 1));
  };

  const turn = (delta: number) => {
    const next = (page + delta + pages) % pages;
    setPage(next);
    setSlots(fillPage(order, count, next));
    setSelected(0);
    setMaximized(null);
    setPresetId(null);
  };

  const applyPreset = (preset: Preset) => {
    setLayout(preset.layout);
    setSlots(preset.slots.slice());
    setSelected(0);
    setMaximized(null);
    setPresetId(preset.id);
  };

  // ── Карусель ──────────────────────────────────────────────────────────────
  // Виды, если их два и больше: это и есть «обход постов». Иначе — страницы камер.
  const cyclesPresets = presets.length >= 2;
  const canCarousel = cyclesPresets || pages > 1;
  const advance = useRef(() => {});
  advance.current = () => {
    if (cyclesPresets) {
      const at = presets.findIndex(x => x.id === presetId);
      applyPreset(presets[(at + 1) % presets.length]);
    } else {
      turn(1);
    }
  };
  useEffect(() => {
    if (!carousel || !canCarousel) {
      return;
    }
    const timer = setInterval(
      () => advance.current(),
      prefs.carouselSeconds * 1000,
    );
    return () => clearInterval(timer);
  }, [carousel, canCarousel, prefs.carouselSeconds]);

  // ── Клавиатура ────────────────────────────────────────────────────────────
  const stageRef = useRef<View>(null);
  const mainRef = useRef<View>(null);
  /**
   * Как в iVMS: Esc сворачивает развёрнутое окно (а потом выходит из режима
   * просмотра), стрелки ходят по окнам, Enter разворачивает выбранное, Delete
   * закрывает его, F — во весь экран (Shift + стрелки — пульт PTZ). Молчит, пока курсор в поле ввода: «F» в поиске
   * камеры — это буква.
   */
  const keys = useRef<(e: KeyboardEvent) => void>(() => {});
  keys.current = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return;
    }
    const arrows: Record<string, 'left' | 'right' | 'up' | 'down'> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down',
    };
    // Shift + стрелки и Shift + «+/−» — пульт PTZ (PtzPanel), не выбор окна.
    if (e.shiftKey && (arrows[e.key] || '+=-_'.includes(e.key))) {
      return;
    }
    if (e.key === 'Escape') {
      if (maximized !== null) {
        setMaximized(null);
      } else if (immersive) {
        setImmersive(false);
      }
    } else if (arrows[e.key] && maximized === null) {
      e.preventDefault();
      setSelected(i => neighbour(shownLayout, i, arrows[e.key]));
    } else if (e.key === 'Enter') {
      setMaximized(m => (m === null ? selected : null));
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      manual();
      setSlots(cur => clearSlot(cur, selected));
      if (maximized === selected) {
        setMaximized(null);
      }
    } else if (
      (e.key === 'f' || e.key === 'F' || e.key === 'а' || e.key === 'А') &&
      CAN_FULLSCREEN
    ) {
      toggleFullscreen(mainRef.current);
    }
  };
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const onKey = (e: KeyboardEvent) => keys.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Свайп по раскладке на телефоне листает страницы ───────────────────────
  const turnRef = useRef(turn);
  turnRef.current = turn;
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const swipe = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) =>
        pagesRef.current > 1 &&
        Math.abs(g.dx) > 24 &&
        Math.abs(g.dx) > Math.abs(g.dy) * 2,
      onPanResponderRelease: (_e, g) => {
        if (g.dx <= -SWIPE) {
          setCarousel(false);
          turnRef.current(1);
        } else if (g.dx >= SWIPE) {
          setCarousel(false);
          turnRef.current(-1);
        }
      },
    }),
  ).current;

  const [stage, setStage] = useState({ w: 0, h: 0 });
  const more = useAnchor();
  const layoutMenu = useAnchor();
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const ptzChannels = usePtzChannelIds();

  if (isPending) {
    return <SkeletonList count={4} avatar={false} />;
  }

  if (isError) {
    return (
      <EmptyState
        tone="error"
        title={t('live.error.list')}
        actionLabel={t('actions.retry')}
        onAction={refetch}
      />
    );
  }

  const addSheet = (
    <AddCameraSheet
      companyId={companyId}
      visible={adding}
      onClose={() => setAdding(false)}
    />
  );

  if (groups.length === 0 && pending.length === 0) {
    return (
      <>
        <EmptyState
          icon="camera"
          title={t('live.empty')}
          hint={t('live.emptyHint')}
          actionLabel={t('camera.add.title')}
          onAction={() => setAdding(true)}
        />
        {addSheet}
      </>
    );
  }

  const { cols, rows, cells } = LAYOUTS[shownLayout];

  /** Окна раскладки в прямоугольнике `w × h`. Окна занимают всё место. */
  const renderGrid = (w: number, h: number) => (
    <View style={[styles.box, { width: w, height: h }]}>
      {cells.map((c, i) => {
        const id = slots[i];
        const camera = id ? byId.get(id) ?? null : null;
        const big = maximized === i;
        const hidden = maximized !== null && !big;
        if (hidden && prefs.economy) {
          return null;
        }
        const frame = big
          ? { left: 0, top: 0, width: w, height: h }
          : {
              left: (c.x * w) / cols,
              top: (c.y * h) / rows,
              width: (c.w * w) / cols,
              height: (c.h * h) / rows,
            };
        const owner = camera ? catalog.ownerOf(camera) : undefined;
        return (
          <View key={i} style={[styles.cell, frame, hidden && styles.hidden]}>
            <LiveTile
              companyId={companyId}
              camera={camera}
              index={i}
              selected={selected === i}
              lastSeen={owner?.last_seen_at ?? owner?.last_sync_at ?? null}
              testID={`live-window-${i}`}
              onPress={() => setSelected(i)}
              onDoublePress={() => {
                setSelected(i);
                setMaximized(m => (m === i ? null : i));
              }}
              onDrop={payload => drop(i, payload)}
              onClose={() => {
                manual();
                setSlots(s => clearSlot(s, i));
                setSelected(i);
                if (maximized === i) {
                  setMaximized(null);
                }
              }}
            />
          </View>
        );
      })}
    </View>
  );

  /**
   * Телефон, «главная камера»: выбранная — во всю ширину сверху, остальные открытые —
   * миниатюрами по три в ряд под ней. Нажатие на миниатюру делает её главной; окна при
   * этом только меняют место (тот же ключ — та же сессия), а не переподключаются.
   * Двойное нажатие по главной — на весь экран.
   */
  const focusWindows = slots
    .slice(0, count)
    .map((id, i) => (id && byId.has(id) ? i : -1))
    .filter(i => i >= 0);
  const focusMain = focusWindows.includes(selected)
    ? selected
    : focusWindows[0] ?? selected;
  const focusThumbs = focusWindows.filter(i => i !== focusMain);
  const FOCUS_COLS = 3;
  const focusHeight = (w: number) => {
    const rowsBelow = Math.ceil(focusThumbs.length / FOCUS_COLS);
    return (w * 9) / 16 + (rowsBelow * ((w / FOCUS_COLS) * 9)) / 16;
  };
  const renderFocus = (w: number) => {
    const mainH = (w * 9) / 16;
    const thumbW = w / FOCUS_COLS;
    const thumbH = (thumbW * 9) / 16;
    const frames = new Map<number, ViewStyle>();
    frames.set(focusMain, { left: 0, top: 0, width: w, height: mainH });
    focusThumbs.forEach((i, n) =>
      frames.set(i, {
        left: (n % FOCUS_COLS) * thumbW,
        top: mainH + Math.floor(n / FOCUS_COLS) * thumbH,
        width: thumbW,
        height: thumbH,
      }),
    );
    return (
      <View style={[styles.box, { width: w, height: focusHeight(w) }]}>
        {[...frames.entries()]
          // Порядок отрисовки — по номеру окна: смена главной не переставляет детей.
          .sort((a, b) => a[0] - b[0])
          .map(([i, frame]) => {
            const id = slots[i];
            const camera = id ? byId.get(id) ?? null : null;
            const owner = camera ? catalog.ownerOf(camera) : undefined;
            return (
              <View key={i} style={[styles.cell, frame]}>
                <LiveTile
                  companyId={companyId}
                  camera={camera}
                  index={i}
                  // Главная — выбранная: у неё кнопки (SD/HD, снимок, ✕) и рамка.
                  selected={i === focusMain}
                  lastSeen={owner?.last_seen_at ?? owner?.last_sync_at ?? null}
                  testID={`live-window-${i}`}
                  onPress={() => setSelected(i)}
                  onDoublePress={() => {
                    setSelected(i);
                    setMaximized(i);
                    setPhoneFull(true);
                  }}
                  onDrop={payload => drop(i, payload)}
                  onClose={() => {
                    manual();
                    setSlots(cur => clearSlot(cur, i));
                  }}
                />
              </View>
            );
          })}
      </View>
    );
  };
  const focus = !isDesktop && prefs.phoneFocus;

  const tool = (
    icon: IconName,
    label: string,
    onPress: () => void,
    testID: string,
    disabled = false,
  ) => (
    <View {...({ title: label } as object)}>
      <IconButton
        icon={icon}
        tone="ghost"
        size={28}
        label={label}
        onPress={onPress}
        disabled={disabled}
        testID={testID}
      />
    </View>
  );

  const layouts = isDesktop ? LAYOUT_IDS : PHONE_LAYOUTS;
  const viewMatches = (x: Preset) =>
    x.layout === layout && sameView(x.slots, slots);
  const activePreset =
    presets.find(x => x.id === presetId) ?? presets.find(viewMatches) ?? null;

  // Пульт PTZ ведёт камеру выбранного окна (на телефоне в режиме «главной» — главную).
  const ptzWindow = maximized ?? (focus ? focusMain : selected);
  const ptzId = slots[ptzWindow];
  const ptzCamera = ptzId ? byId.get(ptzId) ?? null : null;
  // Кнопка «PTZ» — у камеры, поворотность которой не известна: у известной пульт и так
  // открыт, у неповоротной (`ptz: false`) его не бывает.
  // Камера, для которой регистратор открыл канал `ptz`, поворотная наверняка.
  const ptzDirect = !!ptzCamera && ptzChannels.has(ptzCamera.deviceId);
  const ptzToggle =
    ptzCamera &&
    !ptzDirect &&
    !isKnownPtz(ptzCamera) &&
    canPtz(ptzCamera, true);

  const bar = (
    <View
      style={[
        styles.bar,
        { borderTopColor: p.border, backgroundColor: p.card },
      ]}
    >
      {isDesktop
        ? tool(
            'panel',
            t(prefs.panelOpen ? 'live.view.hidePanel' : 'live.view.showPanel'),
            () => setPrefs({ panelOpen: !prefs.panelOpen }),
            'live-panel',
          )
        : null}
      {isDesktop ? (
        <View
          style={[styles.segment, { borderColor: p.border }]}
          testID="live-layouts"
        >
          {layouts.map(id => {
            const on = id === shownLayout && !focus;
            return (
              <Pressable
                key={id}
                testID={`live-layout-${id}`}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                accessibilityLabel={t(`live.view.layout.${id}`)}
                {...({ title: t(`live.view.layout.${id}`) } as object)}
                onPress={() => changeLayout(id)}
                style={state => [
                  styles.layoutBtn,
                  {
                    backgroundColor: on
                      ? p.active
                      : isHot(state)
                      ? p.accent
                      : 'transparent',
                  },
                ]}
              >
                <LayoutGlyph
                  id={id}
                  color={on ? p.foreground : p.mutedForeground}
                />
              </Pressable>
            );
          })}
        </View>
      ) : (
        // На телефоне пять кнопок раскладок — это вся ширина строки. Одна кнопка
        // с рисунком текущей раскладки и меню под ней освобождают строку под главное.
        <View ref={layoutMenu.ref} collapsable={false}>
          <Pressable
            testID="live-layouts"
            accessibilityRole="button"
            accessibilityLabel={t('live.view.layoutTitle')}
            onPress={() => layoutMenu.measure(() => setLayoutOpen(true))}
            style={state => [
              styles.layoutPick,
              { borderColor: p.border },
              isHot(state) && { backgroundColor: p.accent },
            ]}
          >
            {focus ? (
              <FocusGlyph color={p.foreground} />
            ) : (
              <LayoutGlyph id={shownLayout} color={p.foreground} />
            )}
            <Icon name="chevronDown" size={12} color={p.mutedForeground} />
          </Pressable>
        </View>
      )}
      <ViewsMenu
        presets={presets}
        activeId={activePreset?.id ?? null}
        onApply={preset => {
          manual();
          applyPreset(preset);
        }}
        onSave={name => {
          const saved = savePreset(companyId, name, { layout, slots });
          setPresetId(saved.id);
        }}
        onDelete={preset => {
          deletePreset(companyId, preset.id);
          setPresetId(null);
        }}
      />
      <View style={styles.spacer} />
      {pages > 1 && !focus ? (
        <View style={styles.barGroup}>
          {tool(
            'chevronLeft',
            t('live.view.prevPage'),
            () => {
              manual();
              turn(-1);
            },
            'live-prev',
          )}
          <AppText variant="caption" style={styles.pager}>
            {`${page + 1} / ${pages}`}
          </AppText>
          {tool(
            'chevron',
            t('live.view.nextPage'),
            () => {
              manual();
              turn(1);
            },
            'live-next',
          )}
        </View>
      ) : null}
      {canCarousel ? (
        <Pressable
          testID="live-carousel"
          accessibilityRole="button"
          accessibilityState={{ selected: carousel }}
          accessibilityLabel={t(
            carousel ? 'live.view.carouselStop' : 'live.view.carouselStart',
          )}
          {...({
            title: t(
              cyclesPresets
                ? 'live.view.carouselViewsHint'
                : 'live.view.carouselPagesHint',
              { n: prefs.carouselSeconds },
            ),
          } as object)}
          onPress={() => setCarousel(c => !c)}
          style={state => [
            styles.carousel,
            {
              backgroundColor: carousel
                ? p.primarySoft
                : isHot(state)
                ? p.accent
                : 'transparent',
            },
          ]}
        >
          <Icon
            name={carousel ? 'pause' : 'repeat'}
            size={15}
            color={carousel ? p.primaryInk : p.mutedForeground}
          />
          {carousel ? (
            <AppText variant="caption" color={p.primaryInk}>
              {t('live.view.carouselEvery', { n: prefs.carouselSeconds })}
            </AppText>
          ) : null}
        </Pressable>
      ) : null}
      <View style={styles.barGroup}>
        {ptzToggle
          ? tool(
              'ptz',
              t(prefs.ptzOpen ? 'live.ptz.hide' : 'live.ptz.show'),
              () =>
                // На столе пульт живёт в боковой панели — без неё его не видно.
                setPrefs(
                  prefs.ptzOpen
                    ? { ptzOpen: false }
                    : { ptzOpen: true, panelOpen: true },
                ),
              'live-ptz-toggle',
            )
          : null}
        {isDesktop
          ? tool(
              maximized !== null ? 'minimize' : 'maximize',
              t(
                maximized !== null ? 'live.view.restore' : 'live.view.maximize',
              ),
              () => setMaximized(m => (m === null ? selected : null)),
              'live-maximize',
            )
          : null}
        {isDesktop && CAN_FULLSCREEN
          ? tool(
              'fullscreen',
              t('live.view.fullscreen'),
              () => toggleFullscreen(mainRef.current),
              'live-fullscreen',
            )
          : null}
        {!isDesktop
          ? tool(
              'fullscreen',
              t('live.view.fullscreen'),
              () => setPhoneFull(true),
              'live-fullscreen',
            )
          : null}
        <View ref={more.ref} collapsable={false}>
          {tool(
            'more',
            t('live.view.more'),
            () => more.measure(() => setMoreOpen(true)),
            'live-more',
          )}
        </View>
      </View>
      <Menu
        visible={moreOpen}
        onClose={() => setMoreOpen(false)}
        anchor={more.anchor}
        title={t('live.view.more')}
        minWidth={280}
        testID="live-more-menu"
      >
        {/* Редкие действия — здесь, а не кнопками на полосе: «открыть все» и
            «закрыть все» нажимают раз за смену, а места на полосе они занимали
            столько же, сколько раскладка. */}
        <MenuOption
          icon="play"
          label={t('live.view.openAll')}
          selected={false}
          onPress={() => {
            setMoreOpen(false);
            manual();
            setPage(0);
            setSlots(fillPage(order, count, 0));
            setMaximized(null);
            setPresetId(null);
          }}
          testID="live-open-all"
        />
        <MenuOption
          icon="stop"
          label={t('live.view.closeAll')}
          selected={false}
          onPress={() => {
            setMoreOpen(false);
            manual();
            setSlots(emptySlots());
            setSelected(0);
            setMaximized(null);
            setPresetId(null);
          }}
          testID="live-close-all"
        />
        {isDesktop && Platform.OS === 'web' ? (
          <MenuOption
            icon="monitorPlay"
            label={t(
              immersive ? 'live.view.immersiveExit' : 'live.view.immersive',
            )}
            selected={false}
            onPress={() => {
              setMoreOpen(false);
              setImmersive(v => !v);
            }}
            testID="live-immersive"
          />
        ) : null}
        <MenuSeparator />
        <MenuOption
          label={t('live.view.economy')}
          hint={t('live.view.economyHint')}
          selected={prefs.economy}
          onPress={() => setPrefs({ economy: !prefs.economy })}
          testID="live-economy"
        />
        <MenuSeparator />
        <MenuLabel>{t('live.view.carouselStep')}</MenuLabel>
        {CAROUSEL_STEPS.map(n => (
          <MenuOption
            key={n}
            label={t('live.view.seconds', { n })}
            selected={prefs.carouselSeconds === n}
            onPress={() => setPrefs({ carouselSeconds: n })}
          />
        ))}
        {isDesktop ? (
          <>
            <MenuSeparator />
            <View style={styles.help}>
              <AppText variant="label">{t('live.view.helpTitle')}</AppText>
              <AppText variant="caption">{t('live.view.shortcuts')}</AppText>
            </View>
          </>
        ) : null}
      </Menu>
      <Menu
        visible={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        anchor={layoutMenu.anchor}
        title={t('live.view.layoutTitle')}
        testID="live-layout-menu"
      >
        <MenuOption
          leading={<FocusGlyph color={p.foreground} />}
          label={t('live.view.layout.focus')}
          selected={focus}
          onPress={() => {
            setLayoutOpen(false);
            manual();
            setPrefs({ phoneFocus: true });
            setMaximized(null);
            // Миниатюрам нужно место: главная плюс до восьми под ней.
            if (windowCount(layout) < 9) {
              setLayout('9');
            }
          }}
          testID="live-layout-focus"
        />
        {PHONE_LAYOUTS.map(id => (
          <MenuOption
            key={id}
            leading={<LayoutGlyph id={id} color={p.foreground} />}
            label={t(`live.view.layout.${id}`)}
            selected={!focus && id === shownLayout}
            onPress={() => {
              setLayoutOpen(false);
              changeLayout(id);
            }}
            testID={`live-layout-${id}`}
          />
        ))}
      </Menu>
    </View>
  );

  const tree = (scroll: boolean) => (
    <CameraTree
      groups={groups}
      pending={pending}
      openIn={openIn}
      onPick={pick}
      onPickMany={list => {
        manual();
        openMany(list.map(c => c.deviceId));
      }}
      scroll={scroll}
      groupBy={prefs.groupBy}
      onGroupBy={groupBy => setPrefs({ groupBy })}
      onGroupSettings={onOpenSettings}
      onCameraSettings={
        onOpenSettings
          ? c => onOpenSettings(c.peer, c.name || c.deviceId)
          : undefined
      }
      onPendingSettings={
        onOpenSettings
          ? s => onOpenSettings(s.device_id, s.device_name || s.device_id)
          : undefined
      }
      onAdd={() => setAdding(true)}
      onAdopt={onAdopt}
    />
  );

  const ptzPanel = (large: boolean) =>
    ptzCamera && canPtz(ptzCamera, prefs.ptzOpen || ptzDirect) ? (
      <PtzPanel
        companyId={companyId}
        camera={ptzCamera}
        large={large}
        collapsed={prefs.ptzCollapsed}
        onToggle={() => setPrefs({ ptzCollapsed: !prefs.ptzCollapsed })}
      />
    ) : null;

  if (!isDesktop) {
    return (
      <View style={styles.phone} testID="live-view">
        <View
          ref={mainRef}
          style={[styles.phonePanel, { borderColor: p.border }]}
        >
          <View
            ref={stageRef}
            style={styles.phoneStage}
            onLayout={e => {
              const w = e.nativeEvent.layout.width;
              setStage({ w, h: (w * 9) / 16 });
            }}
            {...(focus ? {} : swipe.panHandlers)}
          >
            <View
              style={[
                styles.phoneGrid,
                { height: focus ? focusHeight(stage.w) : stage.h },
              ]}
            >
              {/* Пока раскладка открыта на весь экран, здесь её нет: два набора окон —
                  это два потока с каждой камеры. */}
              {phoneFull
                ? null
                : focus
                ? renderFocus(stage.w)
                : renderGrid(stage.w, stage.h)}
            </View>
          </View>
          {bar}
          {ptzPanel(true)}
        </View>
        <View
          style={[
            styles.phonePanel,
            { borderColor: p.border, backgroundColor: p.card },
          ]}
        >
          {tree(false)}
        </View>
        {addSheet}
        <Modal
          visible={phoneFull}
          animationType="fade"
          supportedOrientations={['portrait', 'landscape']}
          onRequestClose={() => {
            setPhoneFull(false);
            if (focus) {
              setMaximized(null);
            }
          }}
        >
          <View style={styles.full} {...swipe.panHandlers}>
            {renderGrid(win.width, win.height)}
            <View style={styles.fullBar}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('live.view.exitFullscreen')}
                onPress={() => {
                  setPhoneFull(false);
                  if (focus) {
                    setMaximized(null);
                  }
                }}
                style={styles.fullBtn}
                testID="live-full-close"
              >
                <Icon name="close" size={18} color="#ffffff" />
              </Pressable>
            </View>
            {pages > 1 ? (
              <View style={styles.fullPager} pointerEvents="none">
                <AppText variant="caption" color="#ffffff">
                  {`${page + 1} / ${pages}`}
                </AppText>
              </View>
            ) : null}
          </View>
        </Modal>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.desktop,
        { borderColor: p.border, backgroundColor: p.card },
        immersive && fixedFill,
      ]}
      testID="live-view"
    >
      {prefs.panelOpen ? (
        <View
          style={[
            styles.side,
            { borderRightColor: p.border, backgroundColor: p.card },
          ]}
        >
          {tree(true)}
          {ptzPanel(false)}
        </View>
      ) : null}
      {/* Во весь экран уходит раскладка вместе с полосой: без полосы в полноэкранном
          режиме нечем ни сменить раскладку, ни выйти из него мышью. */}
      <View ref={mainRef} style={[styles.main, { backgroundColor: p.card }]}>
        <View
          ref={stageRef}
          style={styles.stage}
          onLayout={e =>
            setStage({
              w: e.nativeEvent.layout.width,
              h: e.nativeEvent.layout.height,
            })
          }
        >
          {renderGrid(stage.w, stage.h)}
        </View>
        {bar}
      </View>
      {addSheet}
    </View>
  );
}
